import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bot, Radio, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { useComms } from '@/context/CommsContext';
import { commsApi } from '@/utils/commsApi';
import { aiApi } from '@/utils/aiApi';
import { conversationKeyFor } from '@/utils/commsFormat';
import { playPressBeep, playReleaseBeep, unlockAudio } from '@/utils/audioCues';
import { useThread } from '@/hooks/useThread';
import { useVoiceRecorder, extForMime } from '@/hooks/useVoiceRecorder';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { ConversationList } from '@/components/comms/ConversationList';
import { ThreadHeader } from '@/components/comms/ThreadHeader';
import { MessageThread } from '@/components/comms/MessageThread';
import { Composer } from '@/components/comms/Composer';
import { PushToTalkButton } from '@/components/comms/PushToTalkButton';
import { CommsHeader, AlertsBar } from '@/components/comms/CommsHeader';
import { InstallCard } from '@/components/comms/InstallCard';
import { EMPTY_FILTERS } from '@/components/comms/SearchFilters';
import '@/styles/comms.css';

const isVisible = () => document.visibilityState === 'visible';

export default function Communications({ user }) {
  const comms = useComms();
  const {
    messages, loading: historyLoading, loadingOlder, hasMore, highlightId, setHighlightId, upload, keyRef,
    reset, loadHistory, loadOlder, jumpTo, upsert, patch, send, retry, uploadAndSend,
  } = useThread(user);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState(EMPTY_FILTERS);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedKey, setSelectedKey] = useState(null);
  const [sendingVoice, setSendingVoice] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const releasedRef = useRef(true);
  const installPrompt = useInstallPrompt();
  const selected = comms.conversations.find((c) => c.key === selectedKey) || null;

  const selectConversation = useCallback((key) => {
    setSelectedKey(key);
    reset(key);
    if (key) {
      unlockAudio();
      return loadHistory(key);
    }
    return Promise.resolve();
  }, [reset, loadHistory]);

  // Search hit → open its conversation, load older pages if needed, highlight the message.
  const openSearchResult = useCallback(async (result) => {
    setSearchQuery('');
    setSearchFilters(EMPTY_FILTERS);
    if (keyRef.current !== result.conversation_key) await selectConversation(result.conversation_key);
    const found = await jumpTo(result.id, result.created_at);
    if (!found) toast('That message could not be located in this conversation');
  }, [keyRef, selectConversation, jumpTo]);

  // Quick reaction toggle: optimistic, then reconciled with the server (and the live echo).
  const toggleReaction = useCallback(async (msg, emoji) => {
    const mineAlready = (msg.reactions || []).some((r) => r.user_id === user.id && r.emoji === emoji);
    patch((m) => m.id === msg.id, (m) => ({
      reactions: mineAlready
        ? (m.reactions || []).filter((r) => !(r.user_id === user.id && r.emoji === emoji))
        : [...(m.reactions || []), { emoji, user_id: user.id, user_name: user.name, created_at: new Date().toISOString() }],
    }));
    try {
      const { reactions } = await commsApi.react(msg.id, emoji);
      patch((m) => m.id === msg.id, { reactions });
    } catch (err) {
      patch((m) => m.id === msg.id, { reactions: msg.reactions || [] });
      toast.error(err?.response?.data?.detail || 'Could not add reaction');
    }
  }, [patch, user.id, user.name]);

  // One-tap snooze on a delivered Vibely AI reminder: reschedule in the device timezone.
  const snoozeReminder = useCallback(async (msg, mode) => {
    if (!msg.reminder_id) return;
    const target = new Date();
    if (mode === 'hour') {
      target.setTime(target.getTime() + 60 * 60 * 1000);
    } else {
      target.setDate(target.getDate() + 1);
      target.setHours(9, 0, 0, 0);
    }
    try {
      await aiApi.snoozeReminder(msg.reminder_id, target.toISOString());
      const when = mode === 'hour'
        ? 'in 1 hour'
        : `tomorrow at ${target.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
      toast.success(`Reminder snoozed — I'll ping you ${when}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not snooze the reminder');
    }
  }, []);

  // Deep link from notifications / toasts: ?c=<conversation key>
  useEffect(() => {
    const target = searchParams.get('c');
    if (!target) return;
    if (target !== keyRef.current) selectConversation(target);
    setSearchParams({}, { replace: true });
  }, [searchParams, selectConversation, setSearchParams, keyRef]);

  useEffect(() => { comms.refreshConversations(); }, [comms.refreshConversations]);

  // "Seen" only counts while this conversation is open and the tab is visible.
  useEffect(() => {
    const sync = () => {
      const active = isVisible() ? selectedKey : null;
      comms.setActive(active);
      if (active) comms.markRead(active);
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      comms.setActive(null);
    };
  }, [selectedKey, comms.setActive, comms.markRead]);

  // Live events for the open conversation
  useEffect(() => comms.subscribe((event) => {
    const key = keyRef.current;
    if (!key) return;
    switch (event.type) {
      case '__connected':
        loadHistory(key);
        break;
      case 'direct_message':
      case 'channel_message': {
        const msg = event.message;
        if (conversationKeyFor(msg, user.id) !== key) return;
        upsert(key, msg);
        if (msg.from_user_id !== user.id && isVisible()) comms.markRead(key);
        break;
      }
      case 'messages_read':
        if (key === `user:${event.reader_id}`) {
          patch((m) => event.message_ids.includes(m.id), { read: true, read_at: event.read_at });
        }
        break;
      case 'channel_read':
        if (key === `team:${event.team_id}`) {
          patch(
            (m) => event.message_ids.includes(m.id) && !(m.read_by || []).includes(event.reader_id),
            (m) => ({ read_by: [...(m.read_by || []), event.reader_id] }),
          );
        }
        break;
      case 'transcript':
        if (conversationKeyFor(event, user.id) === key) {
          patch((m) => m.id === event.message_id, { transcript: event.transcript, transcript_status: event.transcript_status });
        }
        break;
      case 'voice_played':
        if (conversationKeyFor(event, user.id) === key) {
          patch(
            (m) => m.id === event.message_id && !(m.played_by || []).some((p) => p.user_id === event.user_id),
            (m) => ({ played_by: [...(m.played_by || []), { user_id: event.user_id, user_name: event.user_name, played_at: event.played_at }] }),
          );
        }
        break;
      case 'reaction':
        if (conversationKeyFor(event, user.id) === key) {
          patch((m) => m.id === event.message_id, { reactions: event.reactions });
        }
        break;
      default:
        break;
    }
  }), [comms.subscribe, comms.markRead, keyRef, loadHistory, upsert, patch, user.id]);

  const reportPlayed = useCallback((msg) => {
    if ((msg.played_by || []).some((p) => p.user_id === user.id)) return;
    commsApi.markPlayed(msg.id).catch(() => {});
  }, [user.id]);

  // Latest own non-voice message that has been seen (1:1) or seen by others (channel); voice clips use "Played"
  const seen = useMemo(() => {
    const mine = messages.filter((m) => m.from_user_id === user.id && m.status === 'sent' && m.message_type !== 'voice');
    if (selected?.kind === 'team') {
      const last = [...mine].reverse().find((m) => (m.read_by || []).some((id) => id !== user.id));
      return last ? { id: last.id, readers: last.read_by.filter((id) => id !== user.id) } : null;
    }
    const last = [...mine].reverse().find((m) => m.read);
    return last ? { id: last.id, read_at: last.read_at } : null;
  }, [messages, selected?.kind, user.id]);

  // ---------- walkie-talkie ----------
  const recorder = useVoiceRecorder({
    onClip: async ({ blob, duration, mimeType }) => {
      const file = new File([blob], `voice-${Date.now()}.${extForMime(mimeType)}`, { type: mimeType.split(';')[0] });
      setSendingVoice(true);
      try { await uploadAndSend(file, { type: 'voice', duration }); } finally { setSendingVoice(false); }
    },
    onError: (err) => {
      if (err?.message === 'too_short') toast('Hold the button a little longer to send a voice message');
      else if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') toast.error('Microphone access was denied. Allow it in your browser settings to use the walkie-talkie.');
      else if (err?.message === 'unsupported') toast.error('Voice recording is not supported in this browser');
      else toast.error('Could not start recording');
    },
  });

  const handlePttStart = useCallback(() => {
    if (!keyRef.current || !releasedRef.current) return;
    releasedRef.current = false;
    unlockAudio();
    navigator.vibrate?.(50);
    playPressBeep();
    recorder.start();
  }, [recorder, keyRef]);

  const handlePttStop = useCallback(() => {
    if (releasedRef.current) return;
    releasedRef.current = true;
    navigator.vibrate?.(30);
    playReleaseBeep();
    recorder.stop();
  }, [recorder]);

  // ---------- drag & drop ----------
  const onDragOver = (e) => { e.preventDefault(); if (selected) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadAndSend(file);
  };

  return (
    <div className="comms-page" data-testid="communications-page">
      <CommsHeader
        status={comms.status}
        soundsOn={comms.soundsOn}
        onToggleSounds={comms.setSoundsOn}
        quietHours={comms.quietHours}
        quietActive={comms.quietActive}
        onSaveQuietHours={comms.saveQuietHours}
        showBell={comms.alertsBellVisible}
        ttsVoice={comms.ttsVoice}
        onVoiceChange={comms.setTtsVoice}
        readAloud={comms.readAloud}
        onReadAloudChange={comms.setReadAloud}
        onBell={async () => {
          const result = await comms.requestAlerts();
          if (result === 'granted') toast.success('Alerts turned on');
        }}
      />

      {installPrompt.visible && (
        <InstallCard
          ios={installPrompt.ios}
          canPrompt={installPrompt.canPrompt}
          onInstall={installPrompt.install}
          onDismiss={installPrompt.dismiss}
        />
      )}

      {comms.alertsBarVisible && (
        <AlertsBar
          onEnable={async () => {
            const result = await comms.requestAlerts();
            if (result === 'granted') toast.success('Alerts turned on');
          }}
          onDismiss={comms.dismissAlertsBar}
        />
      )}

      {comms.soundBlocked && (
        <button type="button" className="sound-banner" onClick={comms.enableSound} data-testid="enable-sound-button">
          <Volume2 size={16} /> Tap to enable sound for incoming voice messages
        </button>
      )}

      <div className={`comms-layout ${selected ? 'has-selection' : ''}`}>
        <ConversationList
          conversations={comms.conversations}
          loading={comms.loading}
          selectedKey={selectedKey}
          onSelect={selectConversation}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchFilters={searchFilters}
          onSearchFiltersChange={setSearchFilters}
          userId={user.id}
          onOpenResult={openSearchResult}
        />

        <section className="comms-panel comms-thread" data-testid="thread-panel" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
          {selected ? (
            <>
              <ThreadHeader conversation={selected} onBack={() => selectConversation(null)} onToggleMute={comms.toggleMute} />

              <MessageThread
                key={selectedKey}
                messages={messages}
                loading={historyLoading}
                loadingOlder={loadingOlder}
                hasMore={hasMore}
                highlightId={highlightId}
                onLoadOlder={loadOlder}
                onHighlightDone={() => setHighlightId(null)}
                userId={user.id}
                playingId={comms.playingId}
                dragOver={dragOver}
                showSender={selected.kind === 'team'}
                seen={seen}
                members={selected.members}
                onRetry={retry}
                onPlayed={reportPlayed}
                onReact={toggleReaction}
                onSnooze={snoozeReminder}
              />

              {selected.is_bot ? (
                <div className="bot-readonly" data-testid="bot-readonly-note">
                  <Bot size={16} /> Vibely AI delivers your reminders here. Set or cancel them from the <Link to="/dashboard/chat" data-testid="bot-readonly-chat-link">Chat</Link> tab.
                </div>
              ) : (
                <>
                  <Composer
                    disabled={false}
                    upload={upload}
                    onDictationError={(err) => toast.error(err)}
                    onSend={(text) => send({ message: text, message_type: 'text' })}
                    onFile={(file) => uploadAndSend(file)}
                  />

                  <PushToTalkButton
                    onStart={handlePttStart}
                    onStop={handlePttStop}
                    isRecording={recorder.isRecording}
                    elapsed={recorder.elapsed}
                    maxSeconds={recorder.maxSeconds}
                    disabled={sendingVoice}
                    sending={sendingVoice}
                    permission={recorder.permission}
                  />
                </>
              )}
            </>
          ) : (
            <div className="thread-placeholder" data-testid="thread-placeholder">
              <Radio size={40} />
              <p>Pick a channel or teammate to message or talk to.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
